import { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@zoopla-eng/zds-components/box';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';

import { ChangePasswordForm } from '../ChangePasswordForm';
import { WithTrackingData } from '../../AuthModal.types';
import { trackSegmentAndGAEvent } from '@zoopla/tracking';
import { Button } from '@zoopla-eng/zds-components/button';
import { Link } from '@zoopla-eng/zds-components/link';
import { UserWithNextBestAction } from '../../hooks/authMutationHook.types';
import { PictogramCharacters } from '@zoopla/assets';
import {
    successContainerStyle,
    successImageStyle
} from './ChangePasswordStep.styles.css';

export type ChangePasswordStepProps = {
    title?: string;
    previousAccountData: {
        password: string;
        email: string;
    };
    onSkip: () => void;
    onPasswordChangeComplete?: () => Promise<void>;
    onStepCompleted: (user: UserWithNextBestAction) => Promise<void>;
} & WithTrackingData;

export const ChangePasswordStep = ({
    title,
    previousAccountData,
    onStepCompleted,
    onSkip,
    trackingData,
    onPasswordChangeComplete
}: ChangePasswordStepProps) => {
    const headerElement = useRef<HTMLHeadingElement>(null);
    const successHeaderElement = useRef<HTMLHeadingElement>(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [userData, setUserData] = useState<
        UserWithNextBestAction | undefined
    >();

    useLayoutEffect(() => {
        if (headerElement?.current) {
            headerElement?.current.focus();
        }

        trackSegmentAndGAEvent(
            'Module Shown',
            {
                eventCategory: 'signin',
                eventAction: 'module_shown',
                eventLabel: 'secure_password_step'
            },
            trackingData.segmentData.page
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onPasswordChanged = async (user: UserWithNextBestAction) => {
        setUserData(user);
        setIsSuccess(true);

        await onPasswordChangeComplete?.();

        setTimeout(() => {
            successHeaderElement.current?.focus();
        }, 200);
    };

    const onNextClick = () => {
        if (!userData) {
            return;
        }

        onStepCompleted(userData);
    };

    if (isSuccess) {
        return (
            <div className={successContainerStyle}>
                <img
                    src={PictogramCharacters}
                    alt=""
                    loading="lazy"
                    className={successImageStyle}
                />
                <Box pt="S500" pb="S400">
                    <h1
                        className={fontGroups.mug}
                        tabIndex={-1}
                        ref={successHeaderElement}
                    >
                        Success!
                    </h1>
                </Box>
                <Box pb="S400">
                    <p className={fontGroups.ladle}>
                        Your password has been updated
                    </p>
                </Box>
                <Button fw category="primary" onClick={onNextClick}>
                    Continue
                </Button>
            </div>
        );
    }

    return (
        <>
            {title && (
                <Box pb="S400">
                    <h1
                        className={fontGroups.mug}
                        data-testid="change-password-heading"
                        tabIndex={-1}
                        ref={headerElement}
                    >
                        {title}
                    </h1>
                </Box>
            )}

            <Box pb="S400">
                <p className={fontGroups.ladle}>
                    Please update your current password to something a little
                    stronger.
                </p>
            </Box>

            <Box pb="S400">
                <Link
                    href="https://help.zoopla.co.uk/hc/en-gb/articles/15265738301841#why-do-i-need-to-update-my-password--0-0"
                    target="_blank"
                    rel=""
                >
                    Why do I need to do this?
                </Link>
            </Box>

            <Box pb="S400">
                <ChangePasswordForm
                    onCompleted={onPasswordChanged}
                    email={previousAccountData.email}
                    oldPassword={previousAccountData.password}
                    trackingData={trackingData}
                />
            </Box>

            <Box pt="S100" display="flex" justifyContent="center">
                <Button category="tertiary" onClick={onSkip}>
                    Stay logged out
                </Button>
            </Box>
        </>
    );
};
